import { useGenericSurvey } from "features/subscription-process/profiling-process/hooks/useGenericSurvey"
import { PossibleAnswer } from "features/subscription-process/profiling-process/types/interfaces"
import { FC, useMemo } from "react"
import { useAppSelector } from "app/hooks"
import { selectQuestionsAndAnswersMappingWithEligibility } from "features/subscription-process/profiling-process/slices/genericSelectors"
import { Check, X } from "lucide-react"

interface RealEstateEligibilityDetailsProps {
  surveyForm?: Record<string, PossibleAnswer>
}

export const RealEstateEligibilityDetails: FC<RealEstateEligibilityDetailsProps> = ({ surveyForm }) => {
  const { questionWithAnswers } = useGenericSurvey()
  const { eligibilityMessages } = useAppSelector(selectQuestionsAndAnswersMappingWithEligibility)

  const surveyFormData = useMemo(() => questionWithAnswers(surveyForm), [questionWithAnswers, surveyForm])

  // Fonction pour obtenir le message d'éligibilité d'une question
  const getEligibilityMessageForQuestion = (questionKey: string): string | null => {
    if (!surveyForm || !eligibilityMessages[questionKey]) {
      return null
    }

    const questionAnswer = surveyForm[questionKey]
    if (!questionAnswer) {
      return null
    }

    // Helper function to check if an object has a 'key' property
    const hasKeyProperty = (obj: unknown): obj is { key: unknown } => {
      return typeof obj === 'object' && obj !== null && 'key' in obj;
    };

    // Extraire la clé de la réponse
    let answerKey: string
    if (Array.isArray(questionAnswer)) {
      const firstAnswer = questionAnswer[0]
      if (hasKeyProperty(firstAnswer)) {
        answerKey = String(firstAnswer.key)
      } else {
        answerKey = String(firstAnswer)
      }
    } else if (hasKeyProperty(questionAnswer)) {
      answerKey = String(questionAnswer.key)
    } else {
      answerKey = String(questionAnswer)
    }

    return eligibilityMessages[questionKey][answerKey] || null
  }

  // Fonction pour déterminer le statut d'éligibilité d'une question
  const getQuestionStatus = (questionKey: string): 'eligible' | 'non-eligible' | 'neutral' => {
    const message = getEligibilityMessageForQuestion(questionKey)
    return message ? 'non-eligible' : 'eligible'
  }

  return (
    <div className="flex flex-col gap-4">
      {surveyFormData && surveyFormData.map((group, groupIndex) => (
        <div key={groupIndex} className="bg-white rounded-xl">

          {group.questions.map((item, questionIndex) => {
            const questionKey = item.key // ✅ Maintenant nous avons directement accès à la clé

            const eligibilityMessage = getEligibilityMessageForQuestion(questionKey)
            const status = getQuestionStatus(questionKey)

            return (
              <div key={questionIndex} className="mb-4 last:mb-0">
                <div className="flex gap-4 items-start">
                  {/* Icône de statut */}
                  <div className="flex-shrink-0 mt-1">
                    {status === 'eligible' ? (
                      <Check className="w-5 h-5 text-green-600" />
                    ) : (
                      <X className="w-5 h-5 text-red-600" />
                    )}
                  </div>

                  <div className="flex-1">
                    {/* Question */}
                    <label className="font-medium block mb-2">
                      {item.question}
                    </label>

                    {/* Réponses */}
                    <div className="space-y-1 mb-2">
                      {item.answers.map((answer: any, answerIndex: number) => (
                        <span key={answerIndex} className="font-light select-none block text-gray-700">
                          {answer}
                        </span>
                      ))}
                    </div>

                    {/* Message d'éligibilité */}
                    {eligibilityMessage && (
                      <div className={`text-sm p-2 rounded-lg ${status === 'non-eligible'
                        ? 'bg-red-50 text-red-700 border-l-4 border-red-400'
                        : 'bg-green-50 text-green-700 border-l-4 border-green-400'
                        }`}>
                        {eligibilityMessage}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
